import {
    FloatingFocusManager,
    FloatingList,
    autoUpdate,
    useClick,
    useDismiss,
    useFloating,
    useInteractions,
    useListNavigation,
    useRole,
  } from "@floating-ui/react";
  import { useEffect, useRef, useState } from "react";
  import { flip, size, hide } from "@floating-ui/react-dom";
  import classes from "./Dropdown.module.scss";
  import ChevronDown from "./ChevronDown";
  import clsx from "clsx";
  
  export default function DropdownField(props) {
    const { value, onChange, options, className } = props;
    const [isOpen, setIsOpen] = useState(false);
    const [activeIndex, setActiveIndex] = useState(null);
    const [selectedItem, setSelectedItem] = useState(null);

    const { refs, floatingStyles, context, middlewareData } = useFloating({
      whileElementsMounted: autoUpdate,
      strategy: "fixed",
      open: isOpen,
      placement: "bottom-start",
      middleware: [
        flip(),
        size({
          apply({ rects, elements }) {
            Object.assign(elements.floating.style, {
              minWidth: `${rects.reference.width}px`,
            });
          },
        }),
        hide(),
      ],
      onOpenChange: setIsOpen,
    });
  
    const elementsRef = useRef([]);
  
    const handleSelect = (value) => {
      onChange(value);
      setIsOpen(false);
    };
  
    useEffect(() => {
      setSelectedItem(value ?? null);
    }, [value, options]);
  
    const listNav = useListNavigation(context, {
      listRef: elementsRef,
      activeIndex,
      selectedItem,
      onNavigate: setActiveIndex,
    });
  
    const click = useClick(context);
    const dismiss = useDismiss(context);
    const role = useRole(context, { role: "listbox" });
  
    const { getReferenceProps, getFloatingProps, getItemProps } = useInteractions(
      [listNav, click, dismiss, role]
    );
  
    return (
      <>
        <div
          ref={refs.setReference}
          tabIndex={0}
          {...getReferenceProps({
            className: clsx(classes.trigger, selectedItem && classes.filled, className),
          })}
        >
          <div className={classes.label}>{selectedItem ?? <>&nbsp;</>}</div>
  
          <ChevronDown className={classes.triggerIcon} size={30} />
        </div>
  
        {isOpen && (
          <FloatingFocusManager context={context} modal={false}>
            <div
              ref={refs.setFloating}
              style={{
                ...floatingStyles,
                visibility: middlewareData.hide?.referenceHidden
                  ? "hidden"
                  : "visible",
              }}
              {...getFloatingProps({ className: classes.list })}
            >
              <FloatingList elementsRef={elementsRef}>
                {options.map((option, index) => (
                  <button
                    key={option}
                    ref={(element) => (elementsRef.current[index] = element)}
                    role="option"
                    aria-selected={
                      activeIndex === index && selectedItem === option
                    }
                    tabIndex={activeIndex === option ? 0 : -1}
                    className={clsx(classes.item, {
                      [classes.active]: activeIndex === index,
                      [classes.selected]: option === selectedItem,
                    })}
                    {...getItemProps({
                      onClick: () => handleSelect(option),
                    })}
                  >
                    {option}
                  </button>
                ))}
              </FloatingList>
            </div>
          </FloatingFocusManager>
        )}
      </>
    );
  }
  